import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ComponentDefinition } from '../../../../models/ComponentDefinition';
import {
    IDataReferenceState,
    IDateHelperFormatService,
    IDateRangeOptionsBuilder,
    ILoggerService,
    IReferenceStateService,
    IStateService
} from '../../../../services';
import {
    groupRulesAddState,
    groupRulesRemoveState,
    referenceStateRemoved,
    referenceStateUpdated
} from '../../../../common';
import {
    ContextualField,
    ExtendedState,
    Group,
    IDatePickerDates,
    IDateRangePickerOptions,
    IExtraButton,
    Miniature,
    PropertyType,
    StateDto
} from '../../../../models';
import * as Fields from '../../../../common/helpers/contextual';

interface IModernGroupTrendContextualController {
    cancel: () => void;
    data: IDataReferenceState;
    delete: () => void;
    formController: angular.IFormController;
    save: () => void;
}

class ModernGroupTrendContextualController implements IModernGroupTrendContextualController {
    data: IDataReferenceState;
    dateRangePickerOptions: IDateRangePickerOptions;
    extraButtons: IExtraButton[];
    formController: angular.IFormController;
    isLoading: boolean;
    loadingMessage: string;
    miniatures: Miniature[];
    properties: ContextualField[];
    selectedItem: ExtendedState;
    states: StateDto[];
    
    readonly group: Group;

    private _allStates: StateDto[];
    private _usedStates: StateDto[];
    private _watchers: Function[];

    static $inject = [
        '$scope',
        '$rootScope',
        'loggerService',
        'referenceStateService',
        'stateService',
        'dateHelperFormatService',
        'dateRangeOptionsBuilder'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _loggerService: ILoggerService,
        private _referenceStateService: IReferenceStateService,
        private _stateService: IStateService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder
    ) {
    }

    $onInit() {
        if (!this.data) {
            this.data = {} as IDataReferenceState;
        }
        this.setAvailableStates();
        this.initWatchers();
    }

    $onDestroy(): void {
        try {
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach(w => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    openDeleteStateModal(): void {
        $(`#deleteStateModal`).modal('show');
    }

    cancel(): void {
        if (this.data) {
            this.data.selectedItem = null;
        }
        this.formController.$setPristine();
        this._$rootScope.$broadcast(referenceStateUpdated, this.data?.selectedItem);
    }

    save(): angular.IPromise<any> {
        return this._referenceStateService
            .updateOne(this.data.groupGuid, this.data?.selectedItem)
            .then(() => {
                this._loggerService.logSuccess('Reference state saved');
                this.data.selectedItem = undefined;
                this.formController.$setPristine();
                this._$rootScope.$broadcast(referenceStateUpdated, this.data?.selectedItem);
            })
            .catch(reason => {
                this._loggerService.logError('Reference state cannot be saved', true, reason);
            });
    }

    delete(): void {
        this._referenceStateService
            .deleteReferenceState(this.data?.groupGuid, this.data?.selectedItem)
            .then(() => {
                this._loggerService.logSuccess('Reference state deleted');
                this._$rootScope.$broadcast(referenceStateRemoved, this.data?.selectedItem);
                this.data.selectedItem = undefined;
                this.formController.$setPristine();
            })
            .catch(reason => {
                this._loggerService.logError('Reference state cannot be deleted', true, reason);
            });
    }

    getMiniatureList(states: StateDto[]): Miniature[] {
        return states.map((state, index) => new Miniature(state.state.guid, state.state.name, this.getIcon(state.state.icon), [], index, null, state.style));
    }

    private getIcon(icon: string): string {
        console.log(this._stateService.getIcon(icon));
        return this._stateService.getIcon(icon);
    }

    private initWatchers(): void {
        this._$scope.$watch(
            () => this.formController && this.data.selectedItem,
            () => {
                this.configureDeleteButton(this.data.selectedItem);
                if (this.data?.selectedItem?.isNew && this.formController) {
                    this.formController.$setDirty();
                }
            });
        this._$scope.$watch(
            () => this.data && this.data.selectedItem && this.data.selectedItem.beginDateTime && this.data.selectedItem.endDateTime,
            () => {
                this.setRange();
            }
        );
        this._$scope.$watch(
            () => this.data && this.data.groupStates,
            () => {
                if (this.data.groupStates) {
                    this.initDatePicker()
                        .then(() => {
                            this.initProperties();
                        });
                }
            });
        this._watchers = [
            this._$rootScope.$on(groupRulesAddState, (_event, data) => {
                this._stateService.getByGuid(data.itemId).then(state => {
                    this._usedStates.push(state);
                    this.refreshAvailableStates();
                });
            }),
            this._$rootScope.$on(groupRulesRemoveState, (_event, data) => {
                _.remove(this._usedStates, (s) => s.state.guid === data);
                this.refreshAvailableStates();
            })
        ];
    }

    private configureDeleteButton(state: ExtendedState): void {
        if (this.formController && state) {
            this.extraButtons = [
                {
                    click: () => this.openDeleteStateModal(),
                    icon: 'fa-trash-o',
                    isDisabled: state.isNew || this.formController.$invalid,
                    title: 'Delete'
                }
            ];
        }
    }

    private setAvailableStates(): void {
        if (this.data.groupGuid) {
            this._stateService
                .getAll()
                .then(states => {
                    this._allStates = states;
                    return this._stateService.getUsedByGroup(this.data.groupGuid);
                })
                .then(states => {
                    this._usedStates = states;
                    this.refreshAvailableStates();
                });
        }
    }

    private refreshAvailableStates(): void {
        this.states = this._allStates.filter(dto => _.some(this._usedStates, state => state.state.guid === dto.state.guid));
        this.miniatures = this.getMiniatureList(this.states);
    }

    private initDatePicker(): angular.IPromise<void> {
        return this._dateRangeOptionsBuilder
            .getDefaultOptions()
            .then(options => {
                const dateTimeFormat = this._dateHelperFormatService.getDateFormat();
                options.eventHandlers = {
                    'apply.daterangepicker': () => this.applyClick(),
                    'outsideClick.daterangepicker': () => this.applyClick()
                };
                options.locale.format = dateTimeFormat;
                this.dateRangePickerOptions = options;
            });
    }

    private initProperties(): void {
        const dateRange = angular.copy(Fields.DateRange);
        dateRange.extraData.options = this.dateRangePickerOptions;
        const properties: ContextualField[] = [
            dateRange,
            {
                modelName: 'stateGuid',
                propertyType: PropertyType.Select,
                label: 'State',
                placeholder: 'State',
                propertyName: 'stateProperty',
                icon: 'fa-barcode',
                extraData: {
                    options: this.data.groupStates
                        .map(gs => {
                            return { key: gs.state.guid, value: gs.state.name };
                        })
                }
            }, {
                modelName: 'comment',
                propertyType: PropertyType.Documentation,
                label: 'Comment',
                placeholder: 'Comment',
                propertyName: 'commentProperty',
                icon: 'fa-commenting-o',
                extraData: {
                    placement: 'left-top'
                }
            }
        ];
        this.properties = properties;
    }

    private applyClick(): void {
        const range = this.data.selectedItem.dateRange;
        this.dateRangeUpdated(range);
    }

    private setRange(): void {
        if (this.data?.selectedItem) {
            const endDate = new Date(this.data.selectedItem.endDateTime);
            const startDate = new Date(this.data.selectedItem.beginDateTime);
            this.data.selectedItem.dateRange = {
                startDate: moment(startDate),
                endDate: moment(endDate)
            };
        }
    }

    private dateRangeUpdated(range: IDatePickerDates): void {
        if (range) {
            this.data.selectedItem.beginDateTime = range.startDate.toISOString();
            this.data.selectedItem.endDateTime = range.endDate.toISOString();
            if (this.formController) {
                this.formController.$setDirty();
            }
        }
    }
}

export const wtModernGroupTrendContextual: ComponentDefinition = {
    componentId: 'wtModernGroupTrendContextual',
    controller: ModernGroupTrendContextualController,
    template: require('./modernGroupTrendContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        data: '=?',
        formController: '=',
        group: '<'
    }
};